import React, { useState } from 'react';
import './AIAssistant.css';
import { API_BASE } from '../apiBase';
import { StudioIcon } from './StudioIcons';
import { ASSISTANT_TEXT, Locale } from '../studioLocale';

interface Props {
  onSuggestion: (suggestion: string) => void;
  workspacePayload: any;
  selectedId?: string | null;
  selectedSummary?: { id?: string; type?: string; name?: string } | null;
  impactAnalysis?: any;
  contextSize?: number;
  locale: Locale;
}

const ASSISTANT_PROMPT_MAX_LENGTH = 2400;

const AIAssistant: React.FC<Props> = ({
  onSuggestion,
  workspacePayload,
  selectedId,
  selectedSummary,
  impactAnalysis,
  contextSize = 0,
  locale,
}) => {
  const text = ASSISTANT_TEXT[locale];
  const [type, setType] = useState('general');
  const [prompt, setPrompt] = useState('');
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async (quick?: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/ai_suggestions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_payload: workspacePayload,
          selected_id: selectedId || '',
          selected_summary: selectedSummary || undefined,
          impact_analysis: impactAnalysis || undefined,
          user_instruction: `${quick || prompt || text.defaultPrompt}\n${text.replyLanguageHint}`,
          type,
          locale,
          force_llm: true,
        }),
      });
      const data = await response.json();
      const suggestion = String(data?.suggestion || text.noContent);
      setReply(suggestion);
      onSuggestion(suggestion);
    } catch (_error) {
      setReply(text.unavailable);
      onSuggestion(text.unavailable);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-assistant">
      <div className="ai-assistant__header">
        <div className="ai-assistant__header-main">
          <span className="ai-assistant__mark">
            <StudioIcon name="assistant" />
          </span>
          <div>
            <div className="panel-card__eyebrow">{text.eyebrow}</div>
            <h2>{text.title}</h2>
          </div>
        </div>
        <span className="ai-assistant__badge">{text.chars(contextSize)}</span>
      </div>

      <p className="ai-assistant__intro">{text.intro}</p>

      <select className="ai-assistant__select" value={type} onChange={(event) => setType(event.target.value)}>
        {text.types.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <textarea
        className="ai-assistant__textarea"
        value={prompt}
        maxLength={ASSISTANT_PROMPT_MAX_LENGTH}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={text.placeholder}
      />

      <div className="ai-assistant__actions">
        <button className="app-button app-button--strong" type="button" onClick={() => run()} disabled={loading}>
          <span className="app-button__content">
            <StudioIcon name="spark" />
            <span>{loading ? text.running : text.run}</span>
          </span>
        </button>
      </div>

      <div className="ai-assistant__quick-list">
        {text.quick.map((item) => (
          <button key={item} type="button" className="ai-assistant__quick-item" onClick={() => run(item)}>
            <StudioIcon name="quill" />
            <span>{item}</span>
          </button>
        ))}
      </div>

      {reply && <div className="ai-assistant__reply">{reply}</div>}
    </div>
  );
};

export default AIAssistant;
